<template>
  <div class="relative right-0 h-full flex flex-col">
    <div id="chrome-browser-container" class="w-full h-full flex flex-col">
      <!-- Browser Tabs -->
      <div class="tabs">
        <div 
          v-for="(tab, index) in browserTabs" 
          :key="tab.id"
          :class="['tab', { active: activeBrowserTab === tab.id }]"
          :title="tab.title"
          @click="setActiveBrowserTab(tab.id)"
        >
          {{ tab.title }}
        </div>
        <i title="New tab" class="material-icons new-tab" @click="addNewTab">add</i>
      </div>
      
      <!-- Navigation Bar -->
      <nav>
        <i 
          class="material-icons" 
          :class="{ disabled: !canGoBack }"
          title="Click to go back, hold to see history"
          @click="goBack"
        >arrow_back</i>
        <i 
          class="material-icons" 
          :class="{ disabled: !canGoForward }"
          title="Click to go forward, hold to see history"
          @click="goForward"
        >arrow_forward</i>
        <i 
          title="Reload this page" 
          class="material-icons"
          @click="reload"
        >refresh</i>
        <i 
          title="Open the home page" 
          class="material-icons"
          @click="goHome"
        >home</i>
        <form class="search-form" @submit.prevent="navigate">
          <i title="View site information" class="material-icons search-icon" :class="{ 'lock-icon': showIframe }">{{ showIframe ? 'lock' : 'search' }}</i>
          <input 
            class="search" 
            type="search" 
            v-model="currentUrl" 
            placeholder="Search Google or type a URL"
            @keyup.enter="navigate"
          >
          <i title="Bookmark this page" class="material-icons fav-icon">star</i>
        </form>
        <i title="Current user" class="material-icons">account_circle</i>
        <i title="Customize and control Google Chrome" class="material-icons">more_vert</i>
      </nav>
      
      <!-- Content Area -->
      <div class="browser-content-area">
        <iframe 
          v-if="showIframe && !iframeError" 
          :src="iframeUrl" 
          class="browser-frame" 
          frameborder="0"
          allow="fullscreen"
          @load="handleIframeLoad"
          @error="handleIframeError"
        ></iframe>
        <div v-if="iframeError" class="iframe-error-message">
          <p>This page cannot be displayed in an iframe due to security restrictions.</p>
          <p class="error-subtitle">Many search engines block iframe embedding for security reasons.</p>
          <button @click="openInNewWindow" class="open-new-window-btn">Open in New Window</button>
        </div>
        <div class="container" v-else>
          <div class="row header-space"></div>
          <div id="logo-banner" class="row center">
            <img class="logo-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/google-logo-animation.gif">
          </div>
          <div class="row center">
            <div class="search-ber">
            <input id="reset-this-parent" type="text" v-model="searchQuery" placeholder="Search Google or type URL" @keyup.enter="performSearch">
              <div class="mic-wrapper waves-effect">
                <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAACTUlEQVR4Ae2XU7fdQBiGv3Nd27aVmV23QXVT2+4vqG3jX9S2rdvaVpLjM8dIZ8pJ1rYxz1rvNt5nsHcCgjAjEFhWmrIlf4G6Oe++sjk/73fIA3VL/iL2HMQzAzbnN1C2kKu0sOUu8hZyfcj2/IZxPPLO8u4l4nIm2BJhBf0Jey3EG2yd+yvAXhuHAvl5AQjkxuMMWIFECISGEKhIcQEhIATKhUBoCIGyRBMQAkKAnIEsGutvso5D9RAOpzOBI7t//xqGiiwuWZEQeMYL5J2DjsDBTuL9FZA3598Gju8y6sQL6Cp6GgmBy7wAOQszHDMw3+8Z2ELmAIeh4lm8gKmiyxBu8s/AGl6A3j/qPKmn5S75LL+JXAQHpopPOATWhH8GTkJXToClJPsctLIvI1LPh8Ql9hpbeVlqR0uX2ZaQjLtFaiM/dT8LzpnIn/9nT+SysNvsMXCDrqGT9g0sPYNIkX8aRrPitpyGJRAktPByVpqPrknjIv1zeosXyN1X45qhoWUBl1fwUlq4wiFwHyJN4WloSk6Dzspn76lx/f/Gk47rSq9W4ANdQy0NBR11FGdJ/zEUt4BokHsO+uXsrXnJTYkSQ0OHTBVNY5szY4SrqjG4U2V229TwVFr8IHuNu/eZqkuGaKIPkfrRQgYrEEp0FWVw5aPLN6VHM1ribgjlH7pZdtHnhyaNMhT02O/yGn5NrydZAGkQT+iqqyv7FzVU6drXofjdDw2RHxrO/zoEf6CFr1PJjeYQLPHF4xbpwDiLD8Q/QiD6CAEhIBAIfgIDXvSOOYNfugAAAABJRU5ErkJggg==" class="mic-resize">
              </div>
            </div>
          </div>
        <div class="row tab-spacing">
          <div class="tabs-wrapper">
            <ul id="tabs-swipe-demo" class="tabs">
              <li class="tab col s3"><a :class="{ active: activeTab === 1 }" href="#" @click.prevent="setActiveTab(1)"><i class="material-icons">public</i></a></li>
              <li class="tab col s3"><a :class="{ active: activeTab === 2 }" href="#" @click.prevent="setActiveTab(2)"><i class="material-icons">favorite_border</i></a></li>
              <li class="tab col s3"><a :class="{ active: activeTab === 3 }" href="#" @click.prevent="setActiveTab(3)"><i class="material-icons">code</i></a></li>
              <li class="tab col s3"><a :class="{ active: activeTab === 4 }" href="#" @click.prevent="setActiveTab(4)"><i class="material-icons">color_lens</i></a></li>
            </ul>
            <div id="test-swipe-1" v-show="activeTab === 1" class="tab-height col s12">
              <div class="icon-wrapper">
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <span class="notif red lighten-3"></span>
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/gmail.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/drive.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/calender.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/contacts.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/google.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/docs.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/sheets.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/slides.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <span class="notif red lighten-3"></span>
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/forums.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <span class="notif red lighten-3"></span>
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/maps.png">
                  </div>
                </div>
                <div class="detail center"><div class="chip">Frequently visited</div></div>
              </div>
            </div>
            <div id="test-swipe-2" v-show="activeTab === 2" class="tab-height col s12">
              <div class="icon-wrapper">
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <span class="notif red lighten-3"></span>
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/facebook.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/messenger.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <span class="notif red lighten-3"></span>
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/whatsapp.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://cdn2.iconfinder.com/data/icons/social-icons-33/128/Instagram-512.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/youtube.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/cricbuzz.png">
                  </div>
                </div>
              </div>
            </div>
            <div id="test-swipe-3" v-show="activeTab === 3" class="tab-height col s12">
              <div class="icon-wrapper">
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://raw.githubusercontent.com/PraveenAsh/PraveenAsh.github.io/master/static/github.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://cdn2.iconfinder.com/data/icons/social-icons-33/128/Codepen-512.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/HackerRank_logo.png/220px-HackerRank_logo.png">
                  </div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect">
                    <img class="icon-resize" src="https://upload.wikimedia.org/wikipedia/commons/e/e8/HackerEarth_logo.png">
                  </div>
                </div>
              </div>
            </div>
            <div id="test-swipe-4" v-show="activeTab === 4" class="tab-height col s12">
              <div class="icon-wrapper">
                <div class="flat-card">
                  <div class="circle-rep waves-effect yellow"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect orange"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect red"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect pink"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect teal"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect green"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect blue"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect indigo accent-2"></div>
                </div>
                <div class="flat-card">
                  <div class="circle-rep waves-effect grey darken-4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const activeTab = ref(1)
const currentUrl = ref('')
const searchQuery = ref('')
const iframeUrl = ref('')
const showIframe = ref(false)
const activeBrowserTab = ref(1)
const canGoBack = ref(false)
const canGoForward = ref(false)
const iframeError = ref(false)
let tabIdCounter = 1

const browserTabs = ref([
  { id: 1, title: 'New Tab', url: '', history: [], historyIndex: -1 }
])

const setActiveTab = (tabNumber) => {
  activeTab.value = tabNumber
}

const setActiveBrowserTab = (tabId) => {
  activeBrowserTab.value = tabId
  const tab = browserTabs.value.find(t => t.id === tabId)
  if (tab) {
    // Restore tab's history state
    if (tab.history && tab.history.length > 0 && tab.historyIndex >= 0) {
      const currentHistoryUrl = tab.history[tab.historyIndex]
      iframeUrl.value = currentHistoryUrl
      showIframe.value = true
      currentUrl.value = currentHistoryUrl
    } else if (tab.url) {
      iframeUrl.value = tab.url
      showIframe.value = true
      currentUrl.value = tab.url
    } else {
      showIframe.value = false
      currentUrl.value = ''
    }
    updateNavigationButtons()
  }
}

const closeTab = (tabId) => {
  if (browserTabs.value.length <= 1) return
  
  const index = browserTabs.value.findIndex(t => t.id === tabId)
  if (index === -1) return
  
  browserTabs.value.splice(index, 1)
  
  if (activeBrowserTab.value === tabId) {
    const newActiveTab = browserTabs.value[Math.max(0, index - 1)]
    setActiveBrowserTab(newActiveTab.id)
  }
}

const formatUrl = (input) => {
  const trimmed = input.trim()
  if (!trimmed) return ''
  
  try {
    const url = new URL(trimmed)
    return url.href
  } catch (e) {
    if (trimmed.includes('.') && !trimmed.includes(' ')) {
      return `https://${trimmed}`
    }
    // Use Google for searches
    return `https://www.google.com/search?q=${encodeURIComponent(trimmed)}`
  }
}

const navigate = () => {
  let url = formatUrl(currentUrl.value)
  if (!url) return
  
  const activeTab = browserTabs.value.find(t => t.id === activeBrowserTab.value)
  if (activeTab) {
    activeTab.url = url
    try {
      const urlObj = new URL(url)
      activeTab.title = urlObj.hostname.replace('www.', '')
    } catch (e) {
      activeTab.title = 'New Tab'
    }
    
    // Initialize history if not exists
    if (!activeTab.history) {
      activeTab.history = []
      activeTab.historyIndex = -1
    }
    
    // Add to history (remove forward history if we're in the middle)
    activeTab.history = activeTab.history.slice(0, activeTab.historyIndex + 1)
    activeTab.history.push(url)
    activeTab.historyIndex = activeTab.history.length - 1
  }
  
  updateNavigationButtons()
  
  iframeUrl.value = url
  showIframe.value = true
  iframeError.value = false
  
  // Set a timeout to detect if page is blocked
  // If after 2 seconds the iframe hasn't loaded properly, show error
  setTimeout(() => {
    const iframe = document.querySelector('.browser-frame')
    if (iframe && iframeUrl.value === url) {
      // Check if iframe appears to be blocked
      // Unfortunately, X-Frame-Options blocking is hard to detect
      // We'll rely on the error event and user feedback
    }
  }, 2000)
}

const performSearch = () => {
  if (!searchQuery.value.trim()) return
  currentUrl.value = searchQuery.value
  navigate()
}

const addNewTab = () => {
  tabIdCounter++
  const newTab = { id: tabIdCounter, title: 'New Tab', url: '', history: [], historyIndex: -1 }
  browserTabs.value.push(newTab)
  setActiveBrowserTab(newTab.id)
}

const goBack = () => {
  const activeTab = browserTabs.value.find(t => t.id === activeBrowserTab.value)
  if (!activeTab || !activeTab.history || activeTab.historyIndex <= 0) return
  
  activeTab.historyIndex--
  const url = activeTab.history[activeTab.historyIndex]
  iframeUrl.value = url
  currentUrl.value = url
  activeTab.url = url
  showIframe.value = true
    updateNavigationButtons()
}

const goForward = () => {
  const activeTab = browserTabs.value.find(t => t.id === activeBrowserTab.value)
  if (!activeTab || !activeTab.history || activeTab.historyIndex >= activeTab.history.length - 1) return
  
  activeTab.historyIndex++
  const url = activeTab.history[activeTab.historyIndex]
  iframeUrl.value = url
  currentUrl.value = url
  activeTab.url = url
  showIframe.value = true
    updateNavigationButtons()
}

const reload = () => {
  if (showIframe.value && iframeUrl.value) {
    const iframe = document.querySelector('.browser-frame')
    if (iframe) {
      iframe.src = iframe.src
    }
  }
}

const goHome = () => {
  const activeTab = browserTabs.value.find(t => t.id === activeBrowserTab.value)
  if (activeTab) {
    activeTab.url = ''
    activeTab.title = 'New Tab'
    activeTab.history = []
    activeTab.historyIndex = -1
  }
  showIframe.value = false
  currentUrl.value = ''
  iframeUrl.value = ''
  updateNavigationButtons()
}

const updateNavigationButtons = () => {
  const activeTab = browserTabs.value.find(t => t.id === activeBrowserTab.value)
  if (activeTab && activeTab.history) {
    canGoBack.value = activeTab.historyIndex > 0
    canGoForward.value = activeTab.historyIndex < activeTab.history.length - 1
  } else {
    canGoBack.value = false
    canGoForward.value = false
  }
}

const handleIframeError = () => {
  iframeError.value = true
}

const handleIframeLoad = () => {
  // Reset error state when iframe loads
  iframeError.value = false
  
  // Check if iframe is actually blocked (X-Frame-Options)
  // This is a best-effort detection
  setTimeout(() => {
    const iframe = document.querySelector('.browser-frame')
    if (iframe && iframeUrl.value) {
      try {
        // Try to access iframe content - if we can't, it might be blocked
        // But cross-origin always throws, so we check the iframe's natural dimensions
        const rect = iframe.getBoundingClientRect()
        // If iframe has no content height, it might be blocked
        // However, this is not reliable, so we'll rely on the error event
      } catch (e) {
        // Can't access iframe - might be blocked
      }
    }
  }, 1000)
}

const openInNewWindow = () => {
  if (iframeUrl.value) {
    window.open(iframeUrl.value, '_blank', 'noopener,noreferrer')
  }
}

onMounted(() => {
  // Load Roboto font
  const robotoLink = document.createElement('link')
  robotoLink.href = 'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700'
  robotoLink.rel = 'stylesheet'
  document.head.appendChild(robotoLink)
  
  // Load Material Icons font
  const materialIconsLink = document.createElement('link')
  materialIconsLink.href = 'https://fonts.googleapis.com/icon?family=Material+Icons'
  materialIconsLink.rel = 'stylesheet'
  document.head.appendChild(materialIconsLink)
  
  // Inject CSS
  const style = document.createElement('style')
  style.textContent = `
    #chrome-browser-container {
      font-family: 'Roboto', sans-serif;
      background: #fff;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
    }
    
    #chrome-browser-container * {
      font-family: 'Roboto', sans-serif;
    }
    
    /* Browser Tabs */
    #chrome-browser-container .tabs {
      position: relative;
      margin: 0px 5px;
      display: flex;
      align-items: flex-start;
      flex-shrink: 0;
      padding-top: 5px;
      overflow-x: auto;
      overflow-y: hidden;
    }
    
    #chrome-browser-container .tab {
      -webkit-user-select: none;
      padding: 5px;
      padding-left: 15px;
      background-color: white;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      cursor: default;
      font-size: 12px;
      margin-right: 2px;
      min-width: 100px;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    #chrome-browser-container .new-tab {
      -webkit-user-select: none;
      position: relative;
      cursor: default;
      font-size: 18px;
      padding: 0px;
      border-radius: 50%;
      background-color: white;
      transition: .5s;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 5px;
      margin-top: 2px;
    }
    
    #chrome-browser-container .new-tab:hover {
      background-color: rgb(232,234,237);
    }
    
    /* Navigation Bar */
    #chrome-browser-container nav {
      height: 60px;
      width: 100%;
      padding: 10px 5px;
      border-bottom: 1px solid rgba(0,0,0,.2);
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: flex-start;
      background-color: var(--main-bg, rgba(255,255,255, 1));
      color: var(--main, rgba(0, 0, 0, 1));
      flex-shrink: 0;
    }
    
    #chrome-browser-container nav i {
      cursor: default;
      -webkit-user-select: none;
      transition: .5s;
      border-radius: 50%;
      padding: 8px;
      margin-right: 2px;
    }
    
    #chrome-browser-container nav i.disabled {
      color: rgba(0,0,0,.3);
    }
    
    #chrome-browser-container nav i:hover:not(.disabled) {
      background-color: rgba(0,0,0,.1);
    }
    
    #chrome-browser-container .search-form {
      position: relative;
      flex: 5;
      margin-right: 10px;
    }
    
    #chrome-browser-container .search {
      width: 100%;
      border-radius: 15px;
      border: 2px solid transparent;
      background-color: rgb(241,243,244);
      outline: none;
      padding: 5px 40px;
      transition: background-color .1s;
      font-size: 14px;
      font-family: 'Roboto', sans-serif;
    }
    
    #chrome-browser-container .search:hover {
      background-color: rgb(232,234,237);
    }
    
    #chrome-browser-container .search:focus {
      border: 2px solid rgb(179,206,251);
      background-color: transparent;
    }
    
    #chrome-browser-container .search:focus::-webkit-input-placeholder {
      color: transparent;
    }
    
    #chrome-browser-container .search-form i {
      padding: 7px 9px;
    }
    
    #chrome-browser-container .search-icon {
      font-size: .9rem;
      position: absolute;
      left: 4px;
      top: 2px;
    }
    
    #chrome-browser-container .fav-icon {
      font-size: .9rem;
      position: absolute;
      right: 4px;
      top: 2px;
      border-radius: 50%;
      padding: 8px 10px;
      color: rgb(66,134,244);
    }
    
    #chrome-browser-container .fav-icon:hover {
      background-color: rgba(0,0,0,.1);
    }
    
    #chrome-browser-container nav > * {
      margin-right: 10px;
      padding: 2px;
    }
    
    /* Browser Content Area */
    #chrome-browser-container .browser-content-area {
      flex: 1;
      overflow: hidden;
      position: relative;
      background: #fff;
      display: flex;
      flex-direction: column;
    }
    
    #chrome-browser-container .browser-content-area .container {
      flex: 1;
      overflow-y: auto;
    }
    
    #chrome-browser-container .browser-frame {
      width: 100%;
      height: 100%;
      border: none;
    }
    
    #chrome-browser-container .iframe-error-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 20px;
      text-align: center;
      color: rgba(0, 0, 0, 0.7);
    }
    
    #chrome-browser-container .iframe-error-message p {
      margin-bottom: 10px;
      font-size: 16px;
    }
    
    #chrome-browser-container .iframe-error-message .error-subtitle {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.5);
      margin-bottom: 20px;
    }
    
    #chrome-browser-container .open-new-window-btn {
      padding: 10px 20px;
      background-color: rgb(66, 134, 244);
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      transition: background-color 0.2s;
    }
    
    #chrome-browser-container .open-new-window-btn:hover {
      background-color: rgb(52, 120, 220);
    }
    
    #chrome-browser-container .row {
      padding: 0;
      margin: 0;
    }
    
    #chrome-browser-container .header-space {
      height: 8vh;
    }
    
    #chrome-browser-container #logo-banner {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
    
    #chrome-browser-container .logo-resize {
      width: 420px;
      height: auto;
      display: block;
      margin: 0 auto;
    }
    
    #chrome-browser-container .search-ber {
      margin: auto;
      width: 560px;
      height: 44px;
      border-radius: 44px;
      background-color: rgb(241, 243, 244);
    }
    
    #chrome-browser-container .container .row {
      margin-left: 0;
      margin-right: 0;
    }
    
    #chrome-browser-container .mic-wrapper {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      float: right;
      transition: .2s all ease-out;
    }
    
    #chrome-browser-container .mic-wrapper:hover {
      transition: .2s all ease-in;
      background-color: rgba(128, 134, 139, .1);
    }
    
    #chrome-browser-container .mic-resize {
      cursor: pointer;
      margin-top: 10px;
      width: 24px;
      height: 24px;
    }
    
    #chrome-browser-container #reset-this-parent {
      all: initial;
      width: 494px;
      height: 44px;
      float: left;
      margin-left: 22px;
      background-color: transparent;
      font-family: 'Roboto', sans-serif;
    }
    
    #chrome-browser-container input::placeholder {
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      color: rgb(128, 134, 139);
    }
    
    #chrome-browser-container .tab-spacing {
      padding-top: 20px;
      padding-bottom: 20px;
    }
    
    #chrome-browser-container .material-icons {
      font-family: 'Material Icons';
      font-weight: normal;
      font-style: normal;
      font-size: 24px;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      -webkit-font-feature-settings: 'liga';
      -webkit-font-smoothing: antialiased;
      transition: 0.2s all ease;
      color: rgba(128, 128, 128, 0.69);
    }
    
    #chrome-browser-container i {
      transition: 0.2s all ease;
      color: rgba(128, 128, 128, 0.69);
    }
    
    #chrome-browser-container i:hover {
      transition: 0.2s all ease;
      color: #0089ec;
    }
    
    #chrome-browser-container .material-icons:hover {
      color: #0089ec;
    }
    
    #chrome-browser-container .tab-height {
      padding: 0;
      margin: 0;
      height: 200px;
      width: 450px;
    }
    
    #chrome-browser-container .active i {
      color: #0089ec;
    }
    
    #chrome-browser-container .tabs-wrapper {
      margin: auto;
      height: 300px;
      width: 600px;
    }
    
    #chrome-browser-container .tabs {
      height: 36px;
      display: flex;
      list-style: none;
      margin: 0;
      padding: 0;
    }
    
    #chrome-browser-container .tabs li {
      flex: 1;
      text-align: center;
    }
    
    #chrome-browser-container .tabs a {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 36px;
      text-decoration: none;
    }
    
    #chrome-browser-container .tabs .indicator {
      margin: auto;
      position: absolute;
      bottom: 0;
      height: 3px;
      background-color: #0089ec;
      max-width: 50px;
      will-change: left, right;
      border-radius: 15px 15px 0 0;
    }
    
    #chrome-browser-container #test-swipe-1,
    #chrome-browser-container #test-swipe-2,
    #chrome-browser-container #test-swipe-3,
    #chrome-browser-container #test-swipe-4 {
      padding-top: 15px;
      padding-left: 25px;
      padding-right: 25px;
      margin: 0;
    }
    
    #chrome-browser-container .flat-card {
      padding: 30px;
      width: 110px;
      height: 110px;
      float: left;
      position: relative;
    }
    
    #chrome-browser-container .notif {
      width: 12px;
      height: 12px;
      padding: 0;
      margin: 0;
      border-radius: 50%;
      position: absolute;
      right: 0;
      border: 2px solid rgb(241, 243, 244);
      margin-right: 10px;
    }
    
    #chrome-browser-container .icon-resize {
      width: 30px;
      height: 30px;
    }
    
    #chrome-browser-container .icon-wrapper {
      width: 550px;
      height: 100%;
    }
    
    #chrome-browser-container .circle-rep {
      width: 50px;
      height: 50px;
      background-color: rgb(241, 243, 244);
      padding: 10px;
      border-radius: 50%;
    }
    
    #chrome-browser-container .detail {
      font-size: 12px;
    }
    
    #chrome-browser-container .chip {
      background-color: rgba(241, 243, 244, .5);
      font-weight: 400;
    }
    
    #chrome-browser-container .center {
      text-align: center;
    }
    
    #chrome-browser-container .yellow {
      background-color: #fbbc05;
    }
    
    #chrome-browser-container .orange {
      background-color: #ff9800;
    }
    
    #chrome-browser-container .red {
      background-color: #ea4335;
    }
    
    #chrome-browser-container .red.lighten-3 {
      background-color: #ef5350;
    }
    
    #chrome-browser-container .pink {
      background-color: #e91e63;
    }
    
    #chrome-browser-container .teal {
      background-color: #009688;
    }
    
    #chrome-browser-container .green {
      background-color: #34a853;
    }
    
    #chrome-browser-container .blue {
      background-color: #4285f4;
    }
    
    #chrome-browser-container .indigo {
      background-color: #3f51b5;
    }
    
    #chrome-browser-container .grey {
      background-color: #424242;
    }
    
    #chrome-browser-container .darken-4 {
      filter: brightness(0.7);
    }
    
    #chrome-browser-container .accent-2 {
      filter: brightness(1.2);
    }
  `
  document.head.appendChild(style)
  
  // Initialize Materialize tabs if available
  if (window.M && window.M.Tabs) {
    const tabs = document.querySelectorAll('.tabs')
    window.M.Tabs.init(tabs)
  }
})
</script>

